import { Eye, FlaskConical, FolderOpen, Loader2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { MODEL_TABS, tabKind, useActiveTab } from '../state/dock';
import { refKey, useWorkspace } from '../state/workspace';

function ago(at: number): string {
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)} min ago`;
}

export function StatusBar() {
  const source = useWorkspace((s) => s.source);
  const discovery = useWorkspace((s) => s.discovery);
  const busy = useWorkspace((s) => s.busy);
  const lastChange = useWorkspace((s) => s.lastChange);
  const activeRef = useWorkspace((s) => s.activeRef);
  const model = useWorkspace((s) => (s.activeRef ? s.models[refKey(s.activeRef)] : undefined));
  // The model's file, while the tab in front shows that model.
  const modelInFront = useActiveTab((s) => MODEL_TABS.has(tabKind(s.subject) ?? ''));
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 10_000);
    return () => window.clearInterval(id);
  }, []);
  if (!source) return null;
  const migrations = discovery?.sets.reduce((n, s) => n + s.migrations.length, 0) ?? 0;
  const Icon = source.kind === 'folder' ? FolderOpen : source.kind === 'sample' ? FlaskConical : Upload;
  return (
    <footer
      data-testid="status-bar"
      className="flex h-6 flex-none items-center gap-4 overflow-hidden border-t border-border bg-panel px-3 text-[11.5px] whitespace-nowrap text-muted max-md:gap-3"
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon size={12} className="flex-none" /> <span className="truncate">{source.label}</span>
      </span>
      {source.live ? (
        <span className="flex min-w-0 items-center gap-1.5" title="The app notices new, removed and edited files in this folder.">
          <Eye size={12} className="flex-none text-added" />
          <span className="truncate">
            Watching for changes
            {lastChange && (
              <>
                {' '}
                · last change {ago(lastChange.at)}
                <span className="max-md:hidden"> ({lastChange.summary})</span>
              </>
            )}
          </span>
        </span>
      ) : (
        <span className="flex-none" title="A copy was read once. Import again to see changes.">
          Read once
        </span>
      )}
      {busy && (
        <span className="flex min-w-0 items-center gap-1.5">
          <Loader2 size={12} className="flex-none animate-spin" /> <span className="truncate">{busy}</span>
        </span>
      )}
      <span className="ml-auto min-w-0 truncate max-md:hidden">
        {discovery && `${discovery.sets.length} context${discovery.sets.length === 1 ? '' : 's'} · ${migrations} migrations`}
        {activeRef && model && modelInFront && model.file && ` · ${model.file}`}
      </span>
    </footer>
  );
}
